import { createRouter, createWebHistory, RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';
import { canAccessRoute } from '@gigdriver/domain';
import Dashboard from '../pages/Dashboard.vue';
import Login from '../pages/Login.vue';
import OrganizationSelect from '../pages/OrganizationSelect.vue';
import GigsList from '../pages/GigsList.vue';
import GigDetail from '../pages/GigDetail.vue';
import MyOffers from '../pages/MyOffers.vue';
import PeopleList from '../pages/PeopleList.vue';
import RateCardsList from '../pages/RateCardsList.vue';
import ClientsList from '../pages/ClientsList.vue';
import VenuesList from '../pages/VenuesList.vue';
import Settings from '../pages/Settings.vue';
import PayrollList from '../pages/PayrollList.vue';
import TimesheetReview from '../pages/TimesheetReview.vue';
import TimeClock from '../pages/TimeClock.vue';
import MyAccount from '../pages/MyAccount.vue';
import PrivacyPolicy from '../pages/PrivacyPolicy.vue';
import TermsOfService from '../pages/TermsOfService.vue';
import SignContract from '../pages/SignContract.vue';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false },
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: TermsOfService,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/sign',
    name: 'SignContract',
    component: SignContract,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/organizations',
    name: 'OrganizationSelect',
    component: OrganizationSelect,
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/gigs',
    name: 'GigsList',
    component: GigsList,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/people',
    name: 'PeopleList',
    component: PeopleList,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/rate-cards',
    name: 'RateCardsList',
    component: RateCardsList,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/clients',
    name: 'ClientsList',
    component: ClientsList,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/venues',
    name: 'VenuesList',
    component: VenuesList,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/gigs/:gigId',
    name: 'GigDetail',
    component: GigDetail,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/offers',
    name: 'MyOffers',
    component: MyOffers,
    meta: { requiresAuth: true },
  },
  {
    path: '/payroll',
    name: 'PayrollList',
    component: PayrollList,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/timesheets',
    name: 'TimesheetReview',
    component: TimesheetReview,
    meta: { requiresAuth: true, requiresOrg: true },
  },
  {
    path: '/timeclock',
    name: 'TimeClock',
    component: TimeClock,
    meta: { requiresAuth: true },
  },
  {
    path: '/account',
    name: 'MyAccount',
    component: MyAccount,
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(
  async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const authStore = useAuthStore();

    // Wait for auth to be initialized
    if (authStore.isLoading) {
      await new Promise(resolve => {
        const unwatch = authStore.$subscribe(() => {
          if (!authStore.isLoading) {
            unwatch();
            resolve(null);
          }
        });
      });
    }

    const requiresAuth = to.meta.requiresAuth !== false;
    const requiresOrg = to.meta.requiresOrg === true;

    // Redirect to login if not authenticated and route requires auth
    if (requiresAuth && !authStore.isAuthenticated) {
      return next({ name: 'Login', query: { redirect: to.fullPath } });
    }

    // Redirect to login if authenticated but trying to access login — but
    // routes like Privacy/Terms are reachable regardless of auth state, so
    // they're exempt from this bounce.
    if (!requiresAuth && !to.meta.public && authStore.isAuthenticated) {
      return next({ name: 'OrganizationSelect' });
    }

    // Check organization requirement
    if (requiresOrg) {
      const orgStore = useOrganizationStore();
      if (!to.query.org) {
        // Nav links don't always carry the ?org= param — recover it from the
        // route we're leaving or the org store before bouncing to selection.
        const fallbackOrg = (from.query.org as string) || orgStore.currentOrg?.id;
        if (fallbackOrg) {
          return next({ ...to, query: { ...to.query, org: fallbackOrg } });
        }
        return next({ name: 'OrganizationSelect' });
      }
      // orgStore.currentOrg (and myRole, which every page's role-gating
      // depends on) is otherwise only populated by createNewOrganization or
      // by explicitly calling selectOrganization — neither of which runs
      // when navigating straight to a `?org=` link (a bookmark, a full page
      // reload, or the org-picker card's plain router-link). Load it here
      // once per (org, uid) pair so every org-scoped page can rely on it
      // being current — also re-checked against the uid, not just the org
      // id, since signing out and back in as someone else reuses the same
      // Pinia store instance and would otherwise keep serving the previous
      // user's role for an org both happen to belong to.
      if (orgStore.currentOrg?.id !== to.query.org || orgStore.loadedForUid !== authStore.currentUser?.uid) {
        await orgStore.selectOrganization(to.query.org as string);
      }
      // Role gate (packages/domain/src/access.ts): a role that can't use a
      // page is sent to the dashboard rather than shown a permission error.
      if (!canAccessRoute(to.name, orgStore.myRole)) {
        // Staff have no dashboard/gigs — their home is My Offers.
        return orgStore.myRole === 'staff'
          ? next({ name: 'MyOffers' })
          : next({ name: 'Dashboard', query: { org: to.query.org } });
      }
    }

    next();
  },
);

export default router;
